import React from 'react';
import { motion } from 'framer-motion';
import {
    Search, TrendingUp, BarChart3, Globe, Link, FileText,
    Image as ImageIcon, Zap, ShieldCheck, Layout, MessageSquare,
    Share2, MapPin, Gauge, Smartphone, Lock, SearchCode, Database,
    ChevronLeft
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const SEO_Page = () => {
    const onPageItems = [
        "Keyword Optimization", "Meta Tags & Descriptions", "Content Optimization",
        "Internal Linking", "Image Optimization", "URL Structure",
        "Header Tags (H1, H2, H3)", "User Experience (UX) Improvement",
        "Content Readability", "Formatting & Structure"
    ];

    const offPageItems = [
        "High-Quality Backlinks", "Guest Posting", "Social Media Promotion",
        "Influencer Outreach", "Brand Mentions", "Local SEO (Google Profile)",
        "Forum Posting (Quora, Reddit)", "Press Releases", "Directory Submissions"
    ];

    const technicalItems = [
        "Website Speed", "Mobile-Friendly Design", "Secure HTTPS (SSL)",
        "Crawlability & Indexing", "XML Sitemap", "Schema Markup",
        "Canonical Tags", "Robots.txt Optimization", "Fixed Broken Links",
        "Core Web Vitals", "AMP Setup"
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="SEO Services | Rank Higher & Grow Organic Traffic - MehareTech"
                description="Engineered SEO strategies for dominated search rankings. Specialized in On-Page optimization, High-Quality Link Building, and Technical SEO to boost your digital visibility."
                keywords="SEO services India, high quality backlinks, technical SEO audit, organic traffic growth, on page optimization, search engine ranking service, local SEO experts"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <RouterLink to="/services" className="inline-flex items-center gap-2 text-violet-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </RouterLink>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-violet-600/10 blur-[120px] -z-10"></div>
                <div className="absolute bottom-0 left-0 w-1/4 h-full bg-blue-600/10 blur-[100px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-violet-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            SEARCH ENGINE OPTIMIZATION
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            RANK HIGHER, GET <span className="text-gradient">MORE TRAFFIC</span>
                        </h1>
                        <p className="text-lg md:text-xl text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            Boost Your Online Visibility with Expert SEO Services
                        </p>
                        <div className="p-8 bg-white/5 border border-white/10 rounded-[2.5rem] backdrop-blur-xl transition-all hover:border-violet-500/30">
                            <p className="text-base text-slate-300 leading-relaxed max-w-3xl mx-auto">
                                We deliver proven SEO strategies that improve search engine rankings and drive high-quality organic traffic.
                                From keyword research to technical SEO, we cover every aspect for long-term success.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Strategy Section */}
            <section className="py-16 px-6 bg-slate-900/30">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-12">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="flex-1"
                        >
                            <h2 className="text-3xl md:text-4xl font-black mb-4 leading-tight">
                                Smart SEO Strategies
                            </h2>
                            <p className="text-lg text-slate-400 leading-relaxed mb-6">
                                increase your visibility, attract qualified leads, and help your business grow online.
                            </p>
                            <div className="flex items-center gap-4 text-violet-400 font-bold uppercase tracking-widest text-[10px]">
                                <Zap size={16} />
                                Optimize your website, Attract more customers
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="flex-1 grid grid-cols-2 gap-3"
                        >
                            {[
                                { icon: Search, label: 'Analysis' },
                                { icon: TrendingUp, label: 'Strategy' },
                                { icon: BarChart3, label: 'Tracking' },
                                { icon: Globe, label: 'Presence' }
                            ].map((item, i) => (
                                <div key={i} className="bg-white/5 p-6 rounded-2xl border border-white/5 flex flex-col items-center justify-center gap-3 transition-all hover:bg-violet-600/10 hover:border-violet-500/20">
                                    <item.icon size={24} className="text-violet-500" />
                                    <span className="text-[10px] font-black uppercase tracking-widest">{item.label}</span>
                                </div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Core Services Section */}
            <section className="py-16 px-6">
                <div className="container mx-auto">
                    <div className="text-center mb-12">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-3xl md:text-5xl font-black tracking-tighter"
                        >
                            Our Core <span className="text-gradient">SEO Services</span>
                        </motion.h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* On-Page SEO */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="group p-6 bg-slate-900 border border-white/5 rounded-[2rem] hover:border-violet-500/50 transition-all duration-500"
                        >
                            <div className="w-12 h-12 bg-violet-600/20 rounded-xl flex items-center justify-center text-violet-500 mb-6 group-hover:scale-110 group-hover:bg-violet-600 group-hover:text-white transition-all duration-500">
                                <FileText size={24} />
                            </div>
                            <h3 className="text-xl font-black mb-4 uppercase tracking-tight italic">On-Page SEO</h3>
                            <ul className="space-y-2">
                                {onPageItems.map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-slate-400 text-xs group-hover:text-slate-200 transition-colors">
                                        <div className="w-1 h-1 rounded-full bg-violet-500/50 group-hover:bg-violet-500 transition-all"></div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Off-Page SEO */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="group p-6 bg-slate-900 border border-white/5 rounded-[2rem] hover:border-blue-500/50 transition-all duration-500"
                        >
                            <div className="w-12 h-12 bg-blue-600/20 rounded-xl flex items-center justify-center text-blue-500 mb-6 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all duration-500">
                                <Share2 size={24} />
                            </div>
                            <h3 className="text-xl font-black mb-4 uppercase tracking-tight italic">Off-Page SEO</h3>
                            <ul className="space-y-2">
                                {offPageItems.map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-slate-400 text-xs group-hover:text-slate-200 transition-colors">
                                        <div className="w-1 h-1 rounded-full bg-blue-500/50 group-hover:bg-blue-500 transition-all"></div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Technical SEO */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            className="group p-6 bg-slate-900 border border-white/5 rounded-[2rem] hover:border-emerald-500/50 transition-all duration-500"
                        >
                            <div className="w-12 h-12 bg-emerald-600/20 rounded-xl flex items-center justify-center text-emerald-500 mb-6 group-hover:scale-110 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-500">
                                <Database size={24} />
                            </div>
                            <h3 className="text-xl font-black mb-4 uppercase tracking-tight italic">Technical SEO</h3>
                            <ul className="space-y-2">
                                {technicalItems.map((item, i) => (
                                    <li key={i} className="flex items-center gap-2 text-slate-400 text-xs group-hover:text-slate-200 transition-colors">
                                        <div className="w-1 h-1 rounded-full bg-emerald-500/50 group-hover:bg-emerald-500 transition-all"></div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Collaboration Section */}
            <section className="py-16 px-6 bg-white/5 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="flex flex-col lg:flex-row items-center gap-12">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="lg:w-1/2"
                        >
                            <h2 className="text-3xl md:text-4xl font-black mb-6 leading-tight">
                                Collaborative SEO Strategies
                            </h2>
                            <p className="text-base text-slate-400 leading-relaxed font-medium">
                                By working closely with you, we create customized solutions that align with your goals
                                and drive measurable results. we ensure sustainable growth through proven techniques.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="lg:w-1/2 relative group"
                        >
                            <div className="absolute inset-0 bg-violet-600/20 blur-[40px] group-hover:bg-violet-600/30 transition-all duration-700"></div>
                            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-xl">
                                <img
                                    src="https://images.pexels.com/photos/3184431/pexels-photo-3184431.jpeg"
                                    alt="SEO Collaboration"
                                    className="w-full h-48 object-cover transform transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-12 px-6 text-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="max-w-2xl mx-auto"
                >
                    <h3 className="text-2xl md:text-3xl font-black mb-6 leading-tight">
                        Dominate <span className="text-gradient">Google's First Page</span>
                    </h3>
                    <button className="px-10 py-4 bg-violet-600 text-white font-black rounded-full shadow-xl shadow-violet-600/20 hover:scale-105 active:scale-95 transition-all text-[10px] uppercase tracking-widest">
                        Audit My Website Now
                    </button>
                </motion.div>
            </section>
        </div>
    );
};

export default SEO_Page;
